/**
 * App root — handles update check, auth gate, setup wizard, and main shell routing.
 *
 * Flow:
 * 0. Check for updates (pre-auth, non-blocking) → show UpdateScreen ONLY for a
 *    minor/major release; a patch installs itself silently at the next launch
 * 1. Check stored tokens → fetch /auth/me
 * 2. Not authenticated → LoginPage
 * 3. Authenticated but setup incomplete → OnboardingFlow (guided setup)
 * 4. Authenticated + setup complete → AppShell (with react-router)
 */

import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useChatStore } from "@/stores/chatStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { UpdateScreen } from "@/components/onboarding/UpdateScreen";
import { LoginPage } from "@/components/onboarding/LoginPage";
import { OnboardingFlow } from "@/components/onboarding/OnboardingFlow";
import { AppShell } from "@/components/layout/AppShell";
import { ChatView } from "@/components/chat/ChatView";
import { HomeView } from "@/components/home/HomeView";
import { ArtifactGallery } from "@/components/artifacts/ArtifactGallery";
import { SettingsPage } from "@/components/settings/SettingsPage";
import { Spinner } from "@/components/common/Spinner";
import { ToastContainer } from "@/components/common/Toast";
import { InstallBanner } from "@/components/common/InstallBanner";

/** The subset of /updates/check the blocking screen needs handed to it. */
export interface BlockingUpdate {
  update_available: boolean;
  current_version?: string;
  latest_version?: string;
  download_url?: string;
  sha256?: string;
  changelog?: string;
  can_auto_update?: boolean;
  update_kind?: "major" | "minor" | "patch";
  blocking?: boolean;
}

export default function App() {
  const { isAuthenticated, isLoading: authLoading, init: initAuth } = useAuthStore();
  const { setupComplete, isLoading: settingsLoading, loadSettings } = useSettingsStore();
  const connectWs = useChatStore((s) => s.connectWs);
  // Only a MINOR/MAJOR release gates the app (see updater.bump_kind: that is
  // our marker for a schema migration or a moved sync contract, which an old
  // binary must not keep running against). A patch is never allowed to
  // interrupt a launch — Header's poll downloads it silently and cli.py
  // installs it at the next start. Null until the check answers, and the app
  // renders throughout, so being up to date costs nothing.
  const [blockingUpdate, setBlockingUpdate] = useState<BlockingUpdate | null>(null);

  const onUpdateDone = useCallback(() => setBlockingUpdate(null), []);

  // Pre-auth, but NON-blocking: no JWT yet, so raw fetch. Failure is silence —
  // an update check that can't reach S3 must never stand between a user and
  // their app (the old screen made every launch wait up to 3s to learn there
  // was nothing to do).
  useEffect(() => {
    let cancelled = false;
    fetch("/api/updates/check")
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled && d?.update_available && d?.blocking && d?.can_auto_update) {
          setBlockingUpdate(d as BlockingUpdate);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize auth on mount
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // Load settings + connect multiplexed WS once authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadSettings();
      connectWs();
    }
  }, [isAuthenticated, loadSettings, connectWs]);

  // A schema/protocol-bearing release still gates everything — that is the one
  // case where letting someone in would break them in ways they can't diagnose.
  if (blockingUpdate) {
    return <UpdateScreen onDone={onUpdateDone} preloaded={blockingUpdate} />;
  }

  // Loading screen
  if (authLoading || (isAuthenticated && settingsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-jove-10 via-surface to-jove-30">
        <div className="flex flex-col items-center gap-3">
          <Spinner size="lg" className="text-jove" />
          <p className="text-sm text-space-50">Loading OmniWerk...</p>
        </div>
      </div>
    );
  }

  // Not authenticated → login
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Authenticated but setup not complete → guided onboarding
  if (!setupComplete) {
    return <OnboardingFlow />;
  }

  // Fully set up → main app with react-router
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          {/* Default landing is the home dashboard. Existing deep
              links to ``/chat/:id`` keep working unchanged. */}
          <Route index element={<HomeView />} />
          <Route path="home" element={<HomeView />} />
          <Route path="chat/:conversationId" element={<ChatView />} />
          <Route path="artifacts" element={<ArtifactGallery />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      <ToastContainer />
      {/* Mounted here, not on LoginPage/OnboardingFlow: pitching an install to
          someone who hasn't got into the app yet is noise on a phone-sized
          screen. Self-gating — renders nothing off mobile (see InstallBanner). */}
      <InstallBanner />
    </BrowserRouter>
  );
}
